import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Shield, Users, Award, Leaf } from 'lucide-react';
import { companyInfo, faqs } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const About = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container text-center">
          <h1 className="heading-1 mb-6">About NY Pest & Exterminating</h1>
          <p className="body-large max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            New York's trusted eco-friendly pest control experts, dedicated to protecting your home and family
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-wash)', border: '1px solid var(--accent-primary)' }}>
                <Leaf size={16} style={{ color: 'var(--accent-text)' }} />
                <span className="body-small font-semibold" style={{ color: 'var(--accent-text)' }}>
                  Our Mission
                </span>
              </div>
              <h2 className="heading-2 mb-6">Safe, Effective, Eco-Friendly</h2>
              <p className="body-large" style={{ color: 'var(--text-body)' }}>
                At NY Pest & Exterminating, we believe effective pest control shouldn't compromise your family's health or the environment. That's why we've pioneered our <strong>Green Shield Approach</strong> - combining cutting-edge Integrated Pest Management (IPM) techniques with eco-friendly, non-toxic treatments.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <div className="product-card">
                <h3 className="heading-3 mb-3">Our Story</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Founded by local New Yorkers who understand the unique pest challenges of our city, we've built our reputation on reliability, transparency, and results. Every member of our team is licensed, insured, and trained in the latest eco-friendly pest management techniques.
                </p>
              </div>
              
              <div className="product-card">
                <h3 className="heading-3 mb-3">Our Commitment</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  We're committed to same-day service, honest pricing, and complete customer satisfaction. If pests return between treatments, we return for free - no questions asked. Your peace of mind is our guarantee.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Why Choose Us</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              The NY Pest difference
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Leaf size={32} style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="heading-3 mb-2">Eco-Friendly</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                100% safe for children, pets, and the environment
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Shield size={32} style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="heading-3 mb-2">Licensed & Insured</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Fully certified and bonded for your protection
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Users size={32} style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="heading-3 mb-2">Local Experts</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Deep knowledge of NY pest challenges
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Award size={32} style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="heading-3 mb-2">Satisfaction Guaranteed</h3>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Your satisfaction or we return for free
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Frequently Asked Questions</h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Get answers to common questions
              </p>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={faq.id} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    <span className="heading-3" style={{ fontSize: '1.125rem' }}>{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container text-center">
          <h2 className="heading-2 mb-4">Ready to Experience the Difference?</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Join thousands of satisfied New York customers
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phoneRaw}`} className="btn-primary text-lg px-8">
              <Phone size={20} />
              Call {companyInfo.phone}
            </a>
            <Link to="/contact" className="btn-secondary text-lg px-8">
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;